import { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { X, MessageCircle, Sparkles, ChevronRight, HelpCircle, CheckCircle2 } from 'lucide-react';

// Avatar di Simona
const SimonaAvatar = () => (
  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg">
    <span className="text-white text-lg font-bold">S</span>
  </div>
);

// Messaggi contestuali di Simona
const SIMONA_MESSAGES = {
  welcome: {
    text: "Ciao! Sono Simona, la tua assistente virtuale. Ti aiuterò a verificare la tua busta paga passo dopo passo. Inizia caricando il PDF oppure compila manualmente i campi.",
    tips: [
      "Puoi caricare qualsiasi busta paga in formato PDF",
      "L'AI estrarrà automaticamente tutti i dati",
      "Potrai sempre modificare i valori estratti"
    ]
  },
  pdf_uploaded: {
    text: "Ottimo! Hai caricato il PDF. Ora clicca su 'Estrai Dati' e l'AI analizzerà la tua busta paga in pochi secondi.",
    tips: [
      "L'estrazione richiede circa 5-10 secondi",
      "Funziona anche con buste paga scannerizzate",
      "I PDF più nitidi danno risultati migliori"
    ]
  },
  extraction_done: {
    text: "Perfetto! I dati sono stati estratti. Ti consiglio di verificare attentamente i campi evidenziati e, se necessario, seleziona manualmente il CCNL e il livello per un confronto più preciso.",
    tips: [
      "Controlla il lordo e il netto in busta",
      "Verifica che l'IRPEF sia corretta",
      "Seleziona CCNL e livello per verificare i minimi"
    ]
  },
  ccnl_selected: {
    text: "Hai selezionato il CCNL! Ora scegli il tuo livello di inquadramento. Vedrai subito il minimo tabellare previsto dal contratto.",
    tips: [
      "Il livello è indicato sulla busta paga",
      "Confronteremo la tua paga con i minimi CCNL",
      "Eventuali differenze verranno evidenziate"
    ]
  },
  ready_to_submit: {
    text: "Tutto pronto! Clicca su 'Verifica Busta Paga' per avviare l'analisi completa. Controllerò IRPEF, INPS, TFR, straordinari e molto altro.",
    tips: [
      "La verifica è istantanea",
      "Riceverai un report dettagliato",
      "Potrai esportare tutto in PDF"
    ]
  },
  field_help: {
    gross_salary: "La retribuzione lorda è il totale prima delle trattenute. Include paga base, contingenza, straordinari e bonus.",
    base_salary: "La paga base (o minimo tabellare) è stabilita dal tuo CCNL in base al livello di inquadramento.",
    inps_employee: "I contributi INPS a carico del dipendente sono generalmente il 9,19% del lordo.",
    irpef: "L'IRPEF viene calcolata sull'imponibile fiscale con aliquote progressive (23%, 35%, 43%).",
    net_salary: "Il netto in busta è ciò che ricevi effettivamente: Lordo - Trattenute."
  }
};

export default function SimonaAssistant({ 
  stage = 'welcome', 
  onClose,
  isMinimized = false,
  onToggleMinimize,
  highlightedField = null
}) {
  const [currentMessage, setCurrentMessage] = useState(SIMONA_MESSAGES[stage] || SIMONA_MESSAGES.welcome);
  const [showTips, setShowTips] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    setCurrentMessage(SIMONA_MESSAGES[stage] || SIMONA_MESSAGES.welcome);
    setShowTips(false);
  }, [stage]);

  // Mostra help per campo specifico
  useEffect(() => {
    if (highlightedField && SIMONA_MESSAGES.field_help[highlightedField]) {
      setCurrentMessage({
        text: SIMONA_MESSAGES.field_help[highlightedField],
        tips: []
      });
    }
  }, [highlightedField]);

  if (!isVisible) return null;

  if (isMinimized) {
    return (
      <button
        onClick={onToggleMinimize}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform z-50"
        data-testid="simona-minimized"
      >
        <MessageCircle className="w-6 h-6 text-white" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" />
      </button>
    );
  }

  return (
    <Card className="fixed bottom-6 right-6 w-80 shadow-2xl border-2 border-emerald-200 z-50 animate-in slide-in-from-bottom-5" data-testid="simona-assistant">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-4 py-3 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-3">
          <SimonaAvatar />
          <div>
            <h3 className="font-bold text-white">Simona</h3>
            <p className="text-xs text-emerald-100">Assistente BustaControllo</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 text-white hover:bg-white/20"
            onClick={onToggleMinimize}
          >
            <span className="text-lg">−</span>
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 text-white hover:bg-white/20"
            onClick={() => setIsVisible(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Body */}
      <CardContent className="p-4">
        {/* Message */}
        <div className="flex gap-3 mb-4">
          <Sparkles className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-700 leading-relaxed">
            {currentMessage.text}
          </p>
        </div>

        {/* Tips Toggle */}
        {currentMessage.tips && currentMessage.tips.length > 0 && (
          <div>
            <button
              onClick={() => setShowTips(!showTips)}
              className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium mb-2"
            >
              <HelpCircle className="w-4 h-4" />
              {showTips ? 'Nascondi suggerimenti' : 'Mostra suggerimenti'}
              <ChevronRight className={`w-4 h-4 transition-transform ${showTips ? 'rotate-90' : ''}`} />
            </button>

            {showTips && (
              <ul className="space-y-2 pl-2 border-l-2 border-emerald-200">
                {currentMessage.tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-2 text-xs text-gray-600">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-4 pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-400 mb-2">Hai bisogno di aiuto?</p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-xs flex-1"
              onClick={() => window.open('mailto:supporto@bustacontrollo.com', '_blank')}
            >
              Contatta supporto
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
